import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { pricingPlanMessages } from 'src/features/pricing/sites/utils/messages';
import { siteMessages } from 'src/features/sites/utils/messages';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import CellMembershipTypeActions from 'src/features/membershipType/components/cell/CellMembershipTypeActions.tsx';
import CreateMembershipTypeFormModal from 'src/features/membershipType/components/forms/CreateMembershipTypeFormModal.tsx';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { membershipTypeBadgeOptions } from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { MembershipType } from 'src/features/membershipType/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const MembershipTypeTable = () => {
    const { formatMessage, locale } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: true }],
    });

    const manageMembershipTypes = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);

    const { parkingId } = useParking();
    React.useEffect(() => {
        setTitle(formatMessage(membershipTypeMessages.membershipTemplates));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: listMembershipTypes, isLoading } = useListMembershipTypes({
        parkingId,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<MembershipType>();
    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => {
                const templateName = row.original.translations?.find(
                    (membership) => membership.language === locale,
                )?.name;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/membership-type/${row.original.id}`}
                        linkTitle={templateName}
                    />
                );
            },
            meta: {
                title: formatMessage(commonMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('category', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(commonMessages.type),
                badgeOptions: membershipTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('site', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const siteName = row.original.site?.translations?.find((site) => site.language === locale)?.name;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/site/${row.original.site.id}`}
                        linkTitle={siteName}
                        hasPermissions={accessSites}
                    />
                );
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(siteMessages.site),
            },
        }),
        columnHelper.accessor('has_pricing_plan', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) => {
                const hasPricingPlan = !!row.original?.has_pricing_plan;
                return (
                    <Badge>
                        <div className={`size-1.5 rounded-full ${hasPricingPlan ? 'bg-green-600' : 'bg-red-600'}`} />
                        {formatMessage(hasPricingPlan ? generalMessages.yes : generalMessages.no)}
                    </Badge>
                );
            },
            meta: {
                title: formatMessage(membershipTypeMessages.priced),
            },
        }),
        columnHelper.accessor('pricing_plan', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) => {
                const pricingPlanName = row.original.pricing_plan?.translations?.find(
                    (plan) => plan.language === locale,
                )?.name;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/pricing-plan/${row.original.pricing_plan?.id}`}
                        linkTitle={pricingPlanName}
                        hasPermissions={accessPricingPlans}
                    />
                );
            },
            meta: {
                title: formatMessage(pricingPlanMessages.pricingPlan),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => <CellMembershipTypeActions {...props} />,
            maxSize: 45,
        }),
    ];
    return (
        <DataTable<MembershipType, any>
            isLoading={isLoading}
            columns={columns}
            data={listMembershipTypes}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            semantics={{
                title: formatMessage(membershipTypeMessages.membershipTemplates),
                entryName: formatMessage(membershipTypeMessages.template).toLowerCase(),
                entryNames: formatMessage(membershipTypeMessages.templates).toLowerCase(),
                placeholder: formatMessage(commonMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageMembershipTypes && { newEntry: CreateMembershipTypeFormModal }),
            }}
            enableSearchParams={false}
            enableLocalFilters={false}
        />
    );
};

export default MembershipTypeTable;
